import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { companyService } from '@features/companies/services/companyService';
import { useAuthStore } from '../store/authStore';
import { useAuthTracking, trackingHelpers } from '@/services/mixpanel';
import type { LoginRequest, LoginResponse } from '../types/auth.types';
import { getDefaultRouteForRole, normalizeRole, RESTRICTED_DASHBOARD_ROLES } from '@lib/utils/roleNavigation';
import { MESSAGES } from '@lib/constants/messages';

async function ensureSubscriptionInBackground(userEmail: string): Promise<void> {
  try {
    const verification = await companyService.verifySubscription();

    if (!verification.isActive && userEmail !== 'info@hypernovalabs.com') {
      try {
        await companyService.createFreeSubscription();
      } catch (createError) {
        console.error('❌ Failed to create free subscription:', createError);
      }
    }
  } catch (verifyError) {
    console.error('❌ Failed to verify subscription:', verifyError);
  }
}

/** Select a company with a timeout so a slow Aludra never freezes post-login navigation. */
async function selectCompanyWithTimeout(companyId: string, timeoutMs = 10000): Promise<void> {
  await Promise.race([
    companyService.selectCompany(companyId),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Company selection timed out')), timeoutMs)
    ),
  ]);
}

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, setAuth, clearAuth } = useAuthStore();
  const { trackLogin, trackLoginFailed, trackLogout } = useAuthTracking();

  // Extraído del onSuccess del login normal para poder reutilizarlo desde
  // AzureCallbackPage.tsx: cuando el backend logra un login directo con
  // TokenAzure (tenant con confianza AAD configurada en Aludra), el POST
  // /auth/azure/callback responde una sesión completa con la MISMA forma que
  // POST /auth/login (ver auth.controller.ts / authResponseHelper.ts) — hay
  // que aplicarla exactamente igual (guardar auth + navegar según rol/
  // compañías), sin volver a llamar a authService.login.
  const applyLoginSuccess = async (data: LoginResponse) => {
      // Store isSuperAdmin flag in user object
      const userWithAdmin = { ...data.user, isSuperAdmin: data.isSuperAdmin ?? false };
      setAuth(userWithAdmin, data.accessToken, data.sessionId);

      // Track successful login
      trackLogin({
        tenant_id: trackingHelpers.getRealTenantId(),
        user_id: data.user.id,
        full_name: data.user.fullName || `${data.user.firstName} ${data.user.lastName}`,
        roles: data.user.role ? [data.user.role] : ['user'], // Default to 'user' if no role
        company_count: data.companies?.length || 0,
        login_method: 'email',
        environment: import.meta.env.VITE_APP_ENV as any,
      });

      // If user has no companies, show empty state
      if (!data.companies || data.companies.length === 0) {
        console.log('🏢 User has no companies assigned');
        const defaultRoute = getDefaultRouteForRole(data.user.role);
        navigate(defaultRoute, { replace: true });
        return;
      }

      // If user has only one company, auto-select it and go to dashboard
      if (data.companies.length === 1) {
        console.log('🏢 User has only one company, auto-selecting:', data.companies[0].name);

        try {
          // Select the company
          await selectCompanyWithTimeout(data.companies[0].id);
          console.log('✅ Company auto-selected successfully');

          // Navigate based on user role
          const defaultRoute = getDefaultRouteForRole(data.user.role);
          console.log('🔀 Navigating based on role:', {
            originalRole: data.user.role,
            normalizedRole: normalizeRole(data.user.role),
            defaultRoute,
            isRestricted: RESTRICTED_DASHBOARD_ROLES.includes(normalizeRole(data.user.role)),
          });
          navigate(defaultRoute, { replace: true });
          void ensureSubscriptionInBackground(data.user.email);
        } catch (error) {
          console.error('❌ Failed to auto-select company:', error);
          if (data.isSuperAdmin) {
            navigate('/select-company', { replace: true });
          } else {
            // Retry navigation to dashboard even without company selection
            const defaultRoute = getDefaultRouteForRole(data.user.role);
            navigate(defaultRoute, { replace: true });
          }
        }
      } else if (data.isSuperAdmin) {
        // Super admin with multiple companies - go to company selector
        navigate('/select-company', { replace: true });
      } else if (data.companies && data.companies.length > 1) {
        // Regular user with multiple companies - auto-select first company
        console.log('🏢 Regular user with multiple companies, auto-selecting first:', data.companies[0].name);
        try {
          await selectCompanyWithTimeout(data.companies[0].id);

          const defaultRoute = getDefaultRouteForRole(data.user.role);
          navigate(defaultRoute, { replace: true });
          void ensureSubscriptionInBackground(data.user.email);
        } catch (error) {
          console.error('❌ Failed to auto-select company:', error);
          if (data.isSuperAdmin) {
            navigate('/select-company', { replace: true });
          } else {
            const defaultRoute = getDefaultRouteForRole(data.user.role);
            navigate(defaultRoute, { replace: true });
          }
        }
      } else if (data.isSuperAdmin) {
        // Super admin without companies info - go to selector
        navigate('/select-company', { replace: true });
      } else {
        // Fallback for regular users
        const defaultRoute = getDefaultRouteForRole(data.user.role);
        navigate(defaultRoute, { replace: true });
      }
  };

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: applyLoginSuccess,
    onError: (error: any) => {
      // Track failed login
      trackLoginFailed({
        tenant_id: trackingHelpers.getRealTenantId(),
        reason: error?.response?.data?.message || error?.message || MESSAGES.INVALID_CREDENTIALS,
        environment: import.meta.env.VITE_APP_ENV as any,
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Track logout before clearing auth
      if (user) {
        trackLogout({
          tenant_id: trackingHelpers.getRealTenantId(),
          company_id: (user as any).companyId || 0,
          environment: import.meta.env.VITE_APP_ENV as any,
        });
      }

      // Limpiar todo el estado de la aplicación
      clearAuth();
      queryClient.clear(); // Limpiar todo el caché de React Query
      sessionStorage.clear(); // Limpiar session storage
      navigate('/login', { replace: true });
    },
    onError: () => {
      // Limpiar auth incluso si el logout falla
      clearAuth();
      queryClient.clear(); // Limpiar todo el caché de React Query
      sessionStorage.clear(); // Limpiar session storage
      navigate('/login', { replace: true });
    },
  });

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authService.getCurrentUser(),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutos (reducido de 5)
    gcTime: 5 * 60 * 1000, // 5 minutos en cache
    retry: 2, // Reintentar 2 veces si falla
    retryDelay: 1000, // Esperar 1 segundo entre reintentos
  });

  // Proactive token refresh: fires 5 min before the access token expires.
  // Uses raw fetch (not apiClient) so the response interceptor is bypassed —
  // if the proactive refresh fails, the token is still valid and the next
  // regular API call's reactive interceptor handles it gracefully.
  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextRefresh = () => {
      if (timer) clearTimeout(timer);

      const stored = localStorage.getItem('accessToken');
      if (!stored) return;

      try {
        const parts = stored.split('.');
        if (parts.length !== 3) return;
        const payload = JSON.parse(atob(parts[1])) as { exp?: number };
        if (!payload.exp) return;

        const msUntilRefresh = payload.exp * 1000 - Date.now() - 5 * 60 * 1000;
        if (msUntilRefresh <= 0) return;

        timer = setTimeout(async () => {
          try {
            const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';
            const sessionId = localStorage.getItem('sessionId') || '';
            const response = await fetch(`${baseURL}/auth/refresh`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                ...(sessionId ? { 'x-session-id': sessionId } : {}),
              },
            });
            if (response.ok) {
              const data = (await response.json()) as { accessToken: string };
              localStorage.setItem('accessToken', data.accessToken);
              scheduleNextRefresh(); // Reschedule for the new token's expiry
            }
          } catch {
            // Proactive refresh failed — reactive interceptor handles the next 401
          }
        }, msUntilRefresh);
      } catch {
        // Invalid token format — ignore
      }
    };

    scheduleNextRefresh();
    return () => { if (timer) clearTimeout(timer); };
  }, [isAuthenticated]);

  return {
    user: currentUser || user,
    isAuthenticated,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    // Para AzureCallbackPage.tsx: aplica una respuesta de sesión completa que
    // YA llegó (login directo con TokenAzure en el callback de Azure AD), sin
    // volver a llamar a authService.login.
    applyLoginSuccess,
  };
}
